import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { countryFormSchema, type CountryFormValues } from './countryFormSchema'
import type { Country, CountryUpsert } from '../types'

interface Props {
  visible: boolean
  initial: Country | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: CountryUpsert) => void
  onHide: () => void
}

/** Ülke ekle/düzenle formu (026: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function CountryFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(countryFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: CountryFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={
        initial ? t('location.countries.form.editTitle') : t('location.countries.form.newTitle')
      }
      submitLabel={t('location.countries.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('location.countries.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('location.countries.form.nameEn')}
        />
      </div>
    </FormDialog>
  )
}
